/**
 * The shape table, shown as pictures.
 *
 * Every shape in the vocabulary drawn as a preview, with its fields listed and
 * a button that puts one into the sprite you have open. It is written straight
 * out of `SHAPES` rather than kept in step by hand, so a shape added to the
 * library appears here without anybody remembering to add it.
 *
 * It borrows five things from the workbench and nothing else, which is why it
 * could come out of a file of two thousand lines. Those five are handed in
 * rather than reached for, so this file cannot quietly grow a sixth.
 */

import { GLYPHS, type Kit } from "../src/kit.js";
import { bitmapOf } from "../src/render.js";
import type { Rgba } from "../src/raster.js";
import { asSpec } from "./bundled.js";
import { SHAPES } from "../src/shapes.js";
import type { Part, SpriteSpec } from "../src/spec.js";
import { blit } from "./paint.js";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const shapeList = $<HTMLDivElement>("shapes-grid");

/** What this needs of the sprite somebody has open. */
export interface Workbench {
	readSpec: () => SpriteSpec | null;
	kitOf: (spec: SpriteSpec) => Kit | null;
	setSpec: (spec: SpriteSpec, keepState?: boolean) => void;
	/** Say something went wrong, where the person is looking. */
	fail: (message: string) => void;
	/** Which part the inspector should open, once one has been added. */
	setEditing: (index: number) => void;
}


/**
 * A kit that exists only to draw the previews.
 *
 * Every role a shape's DEFAULTS reach for has to be in here, not merely the
 * ones the previews name. That is the same trap the model falls into: a shape
 * only asks for its defaulted role when it draws that feature, so a missing
 * "rim" surfaces as one preview failing rather than as an obvious mistake.
 */
const DEMO_KIT = {
	name: "preview",
	roles: {
		none: [0, 0, 0, 0],
		outline: [16, 22, 34, 255],
		base: [88, 132, 176, 255],
		lit: [148, 190, 226, 255],
		shade: [52, 84, 118, 255],
		accent: [224, 168, 88, 255],
		glass: [170, 210, 230, 110],
		// The names shapes fall back to when a field is left out.
		casing: [88, 132, 176, 255],
		casing_lit: [148, 190, 226, 255],
		casing_shadow: [52, 84, 118, 255],
		rim: [148, 190, 226, 255],
		recess: [52, 84, 118, 255],
		trim: [16, 22, 34, 255],
		// Not in any field table. The renderer reaches for it directly when a
		// shape draws a specular highlight, which is why meter and lamp had no
		// preview until it was added.
		white: [255, 255, 255, 255],
	} as Record<string, Rgba>,
	ramps: { ramp: ["shade", "base", "lit"] },
};

const DEMO_SIZE = 26;

/**
 * One shape, drawn on its own, big enough to recognise.
 *
 * Built from the shape's own field table rather than a hand-written example per
 * shape, so a new shape gets a preview the day it is added and an old one
 * cannot quietly show something it no longer does. Numeric defaults are kept,
 * because they are what the shape was designed around; only the colours are
 * replaced, so the whole set reads as one family.
 */
function demoSpec(name: string): SpriteSpec {
	const part: Record<string, unknown> = { shape: name };

	/**
	 * Colours are handed out so that no two role fields on one shape match.
	 *
	 * Naming them semantically was the first attempt and it left hazard, studs
	 * and half a dozen others as a single flat rectangle, because their two role
	 * fields both fell through to the same default. A preview that cannot be told
	 * from its neighbour is the list this replaced.
	 */
	let next = 0;
	const spread = () => ["base", "accent", "shade", "lit"][next++ % 4];

	for (const [field, def] of Object.entries(SHAPES[name].fields)) {
		switch (def.type) {
			case "role":
				part[field] = /lit|highlight/.test(field)
					? "lit"
					: /shadow/.test(field)
						? "shade"
						: /border|outline|divider/.test(field)
							? "outline"
							: spread();
				break;
			case "roles":
				part[field] = ["shade", "base", "lit"];
				break;
			case "ramp":
				part[field] = "ramp";
				break;
			case "glyph":
				part[field] = "bolt";
				break;
			case "box":
				part[field] = [3, 3, 3, 3];
				break;
			case "point":
				part[field] = [DEMO_SIZE / 2, DEMO_SIZE / 2];
				break;
			case "value":
				// Not 1, which fills a meter completely and hides what it does.
				part[field] = 0.65;
				break;
			default:
				// A number, an int, a bool or an enum: the shape's own default is the
				// value it was designed around, so it is left alone.
				if (def.required) {
					part[field] =
						def.type === "bool" ? true : def.type === "edge" ? "top" : def.type === "enum" ? def.values?.[0] : 2;
				}
		}
	}
	// A "from" and "to" pair of POINTS wants to cross the canvas rather than sit
	// in a corner. On gradient the same two names are roles, which is why this
	// asks the field table rather than the field name.
	const fields = SHAPES[name].fields;
	if (fields.from?.type === "point") part.from = [4, DEMO_SIZE - 6];
	if (fields.to?.type === "point") part.to = [DEMO_SIZE - 5, 5];

	return asSpec({ name: `demo_${name}`, size: [DEMO_SIZE, DEMO_SIZE], kit: DEMO_KIT, parts: [part] });
}

/** One glyph, drawn on its own, using the shape that draws glyphs. */
function glyphSpec(glyph: string): SpriteSpec {
	return asSpec({
		name: `glyph_${glyph}`,
		size: [16, 16],
		kit: DEMO_KIT,
		outline: false,
		parts: [{ shape: "glyph", at: [8, 8], id: glyph, role: "lit" }],
	});
}

/**
 * The vocabulary, drawn rather than listed.
 *
 * It was 22 collapsible names and a line of comma-separated glyph names, which
 * is a strange thing for a tool whose whole argument is a closed set of shapes:
 * the one page that could show what they look like was describing them instead.
 *
 * Anything that fails to draw is dropped rather than left as a blank tile. A
 * missing preview is a bug in this function, not something a visitor should be
 * asked to puzzle over.
 */
export function buildReference(shows: Workbench) {
	const grid = document.createElement("div");
	grid.className = "shape-grid";

	let picked: string | null = null;
	const detail = document.createElement("div");
	detail.className = "shape-detail";
	detail.hidden = true;

	const show = (name: string) => {
		picked = name;
		const fields = document.createElement("dl");
		for (const [field, def] of Object.entries(SHAPES[name].fields)) {
			const dt = document.createElement("dt");
			dt.textContent = field;
			const dd = document.createElement("dd");
			const type = def.type === "enum" ? def.values?.join(" | ") : def.type;
			dd.textContent = def.required ? `${type}, required` : `${type}, default ${JSON.stringify(def.default)}`;
			fields.append(dt, dd);
		}
		const title = document.createElement("p");
		title.className = "shape-name";
		title.textContent = name;

		const add = document.createElement("button");
		add.type = "button";
		add.className = "btn add";
		add.textContent = `Add a ${name} to this sprite`;
		add.addEventListener("click", () => insertPart(shows, name));

		detail.replaceChildren(title, fields, add);
		detail.hidden = false;
		for (const tile of grid.children) {
			tile.setAttribute("aria-pressed", String((tile as HTMLElement).dataset.shape === name));
		}
	};

	for (const name of Object.keys(SHAPES)) {
		let bmp: { width: number; height: number; data: Uint8Array };
		try {
			bmp = bitmapOf(demoSpec(name));
		} catch (err) {
			console.error("no preview for", name, err instanceof Error ? err.message : err);
			continue;
		}
		const tile = document.createElement("button");
		tile.type = "button";
		tile.className = "shape-tile checker";
		tile.dataset.shape = name;
		tile.setAttribute("aria-pressed", "false");
		tile.title = name;

		const c = document.createElement("canvas");
		blit(c, bmp, 2);
		const label = document.createElement("span");
		label.textContent = name;
		tile.append(c, label);
		tile.addEventListener("click", () => show(name));
		grid.append(tile);
	}

	const glyphHead = document.createElement("p");
	glyphHead.className = "note glyph-head";
	glyphHead.textContent = "Glyphs. Click one to stamp it on the sprite, in the middle, where you can move it.";

	/*
	 * Eight glyphs you can actually use.
	 *
	 * These were spans: eight little pictures with names under them and no way to
	 * put one on a sprite short of finding the glyph shape, adding it, and knowing
	 * that the field naming the mark is called `id`. Every other preview in this
	 * panel adds its shape when you click it, so these do too.
	 */
	const glyphs = document.createElement("div");
	glyphs.className = "glyph-grid";
	for (const glyph of Object.keys(GLYPHS).sort()) {
		try {
			const bmp = bitmapOf(glyphSpec(glyph));
			const cell = document.createElement("button");
			cell.type = "button";
			cell.className = "glyph-cell checker";
			cell.title = `Add the ${glyph} glyph to this sprite`;
			const c = document.createElement("canvas");
			blit(c, bmp, 2);
			const label = document.createElement("span");
			label.textContent = glyph;
			cell.append(c, label);
			cell.addEventListener("click", () => insertPart(shows, "glyph", { id: glyph }));
			glyphs.append(cell);
		} catch (err) {
			console.error("no preview for glyph", glyph, err instanceof Error ? err.message : err);
		}
	}

	shapeList.append(grid, detail, glyphHead, glyphs);
	if (picked === null) show(Object.keys(SHAPES)[0]);
}

/**
 * Append a part with every required field filled in with something legal, so
 * the sprite still renders the instant it is added. An insert that breaks the
 * preview teaches nothing.
 */
function insertPart(shows: Workbench, shapeName: string, overrides: Record<string, unknown> = {}) {
	const spec = shows.readSpec();
	if (!spec) return shows.fail("Fix the JSON before adding a part.");
	const kit = shows.kitOf(spec);
	if (!kit) return shows.fail("Fix the kit before adding a part.");
	const firstRole = Object.keys(kit.roles).find((r) => r !== "none") ?? "outline";
	const [w, h] = spec.size ?? [32, 32];
	const part: Record<string, unknown> = { shape: shapeName };
	for (const [field, def] of Object.entries(SHAPES[shapeName].fields)) {
		if (!def.required) continue;
		if (def.type === "box") part[field] = [2, 2, 2, 2];
		else if (def.type === "point") part[field] = [Math.floor(w / 2), Math.floor(h / 2)];
		else if (def.type === "role") part[field] = firstRole;
		else if (def.type === "roles") part[field] = [firstRole, firstRole, firstRole];
		else if (def.type === "ramp") part[field] = Object.keys(kit.ramps)[0];
		else if (def.type === "glyph") part[field] = Object.keys(GLYPHS)[0];
		else if (def.type === "value") part[field] = 1;
		else if (def.type === "int" || def.type === "number") part[field] = 1;
		else if (def.type === "bool") part[field] = false;
		else if (def.type === "edge") part[field] = "top";
		else if (def.type === "enum") part[field] = def.values?.[0];
	}
	// Which mark, which edge: whatever the caller already knows, on top of the
	// defaults. Clicking the bolt glyph should add a bolt, not the first glyph in
	// the list with a note to go and change it.
	Object.assign(part, overrides);
	// Open it straight away. A new part lands with placeholder numbers on top of
	// everything else, so the next thing anybody wants is to change them.
	shows.setEditing(spec.parts.length);
	shows.setSpec({ ...spec, parts: [...spec.parts, part as Part] }, true);
	$<HTMLButtonElement>("tab-parts").click();
}
